/**
 * Analysis route (`/analysis`). Replaces the legacy Jinja
 * `templates/analysis.html` + `static/analysis.js` setup.
 *
 * React state owns the selected range (and optional custom start/end), with
 * sessionStorage persistence so navigation away from Analysis keeps the
 * current selection for this browser session. `useAnalysis()` reacts to those
 * changes and fetches `/api/analysis`. The query is disabled until both custom
 * dates are supplied so we never trigger the backend's 400 path.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import { useAnalysis } from "../../api/queries";
import { useActiveCar } from "../../auth/useActiveCar";
import Icon from "../../components/Icon";
import type { RangeKey } from "../../api/types";
import MetricChips from "./MetricChips";
import RangePicker from "./RangePicker";
import PriceTrendChart from "./charts/PriceTrendChart";
import MonthlyBarChart from "./charts/MonthlyBarChart";
import DailyBarChart from "./charts/DailyBarChart";
import ConsumptionChart from "./charts/ConsumptionChart";
import FuelTypeChart from "./charts/FuelTypeChart";
import CountriesChart from "./charts/CountriesChart";
import RawDataCharts from "./charts/RawDataCharts";
import { usePalette } from "./charts/usePalette";

const RANGE_STORAGE_KEY = "wasschluckter.analysis.range";
const RANGE_KEYS = new Set<RangeKey>([
  "month",
  "last_month",
  "ytd",
  "last_12_months",
  "all_time",
  "custom",
]);

interface RangeSelection {
  range: RangeKey;
  start?: string;
  end?: string;
}

function readStoredRange(): RangeSelection {
  const fallback: RangeSelection = { range: "month" };
  if (typeof window === "undefined") return fallback;

  try {
    const raw = window.sessionStorage.getItem(RANGE_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<RangeSelection>;
    if (!parsed.range || !RANGE_KEYS.has(parsed.range)) return fallback;

    return {
      range: parsed.range,
      start: typeof parsed.start === "string" ? parsed.start : undefined,
      end: typeof parsed.end === "string" ? parsed.end : undefined,
    };
  } catch {
    return fallback;
  }
}

function isShortPeriod(start: string, end: string): boolean {
  const diffMs = new Date(end).getTime() - new Date(start).getTime();
  return diffMs <= 31 * 24 * 60 * 60 * 1000;
}

function storeRange(selection: RangeSelection): void {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(RANGE_STORAGE_KEY, JSON.stringify(selection));
  } catch {
    // Session persistence is a convenience; the page still works without it.
  }
}

export default function AnalysisPage() {
  const { hasCars, isLoading: activeCarLoading } = useActiveCar();
  const [selection, setSelection] = useState<RangeSelection>(readStoredRange);

  const palette = usePalette();
  const analysis = useAnalysis(selection.range, selection.start, selection.end);

  if (activeCarLoading) {
    return (
      <section className="page-section">
        <p className="empty-state">Loading…</p>
      </section>
    );
  }

  if (!hasCars) {
    return (
      <section className="page-section">
        <div className="empty-state">
          <p>You don't have any cars yet.</p>
          <p>Add one in the Garage to start logging fuel.</p>
          <Link to="/garage" className="button button--primary">
            Go to Garage
          </Link>
        </div>
      </section>
    );
  }

  const onRangeChange = (
    next: RangeKey,
    nextStart?: string,
    nextEnd?: string,
  ) => {
    const nextSelection = {
      range: next,
      start: nextStart,
      end: nextEnd,
    };
    setSelection(nextSelection);
    storeRange(nextSelection);
  };

  return (
    <>
      <section className="page-section">
        <header className="section-header section-header--with-control">
          <h1 className="section-header__title">
            Analysis
            {analysis.data ? (
              <span className="section-header__meta"> · {analysis.data.range_label}</span>
            ) : null}
          </h1>
        </header>

        <RangePicker
          value={selection.range}
          start={selection.start}
          end={selection.end}
          onChange={onRangeChange}
        />
      </section>

      {analysis.isLoading ? (
        <section className="page-section">
          <p className="empty-state">Loading…</p>
        </section>
      ) : analysis.isError ? (
        <section className="page-section">
          <p className="empty-state">
            Could not load analysis: {analysis.error?.message ?? "unknown error"}.
          </p>
        </section>
      ) : analysis.data ? (
        <>
          <section className="page-section">
            <details className="raw-data" open>
              <summary className="raw-data__summary">
                <span className="raw-data__title">Raw data</span>
                <Icon name="chevron" className="raw-data__summary-icon" />
              </summary>
              <RawDataCharts
                fuelPoints={analysis.data.charts.raw_fuel_log ?? []}
                odometerPoints={analysis.data.charts.raw_odometer_log ?? []}
                start={analysis.data.start}
                end={analysis.data.end}
              />
            </details>
          </section>

          <section className="page-section">
            <MetricChips
              metrics={analysis.data.metrics}
              isLongPeriod={!isShortPeriod(analysis.data.start, analysis.data.end)}
            />
          </section>

          <section className="page-section">
            <div className="chart-grid">
              <article className="chart-card">
                <h3 className="chart-card__title">Average fuel price per liter</h3>
                <div className="chart-card__canvas">
                  <PriceTrendChart points={analysis.data.charts.price_trend} />
                </div>
              </article>

              {isShortPeriod(analysis.data.start, analysis.data.end) ? (
                <>
                  <article className="chart-card">
                    <h3 className="chart-card__title">Liters purchased per day</h3>
                    <div className="chart-card__canvas">
                      <DailyBarChart
                        points={analysis.data.charts.daily_liters}
                        label="Liters"
                        color={palette.accent}
                        unit="L"
                      />
                    </div>
                  </article>

                  <article className="chart-card">
                    <h3 className="chart-card__title">Spending per day</h3>
                    <div className="chart-card__canvas">
                      <DailyBarChart
                        points={analysis.data.charts.daily_spending}
                        label="€"
                        color={palette.success}
                        unit="€"
                      />
                    </div>
                  </article>
                </>
              ) : (
                <>
                  <article className="chart-card">
                    <h3 className="chart-card__title">Liters purchased per month</h3>
                    <div className="chart-card__canvas">
                      <MonthlyBarChart
                        points={analysis.data.charts.monthly_liters}
                        label="Liters"
                        color={palette.accent}
                        unit="L"
                      />
                    </div>
                  </article>

                  <article className="chart-card">
                    <h3 className="chart-card__title">Spending per month</h3>
                    <div className="chart-card__canvas">
                      <MonthlyBarChart
                        points={analysis.data.charts.monthly_spending}
                        label="€"
                        color={palette.success}
                        unit="€"
                      />
                    </div>
                  </article>
                </>
              )}

              <article className="chart-card">
                <h3 className="chart-card__title">Consumption (L/100km, estimated)</h3>
                <div className="chart-card__canvas">
                  <ConsumptionChart
                    points={analysis.data.charts.consumption_series}
                  />
                </div>
              </article>

              <article className="chart-card">
                <h3 className="chart-card__title">Fuel types</h3>
                <div className="chart-card__canvas">
                  <FuelTypeChart points={analysis.data.charts.fuel_type_summary} />
                </div>
              </article>

              {analysis.data.charts.country_breakdown.length > 1 ? (
                <article className="chart-card">
                  <h3 className="chart-card__title">Average price per country</h3>
                  <div className="chart-card__canvas">
                    <CountriesChart
                      points={analysis.data.charts.country_breakdown}
                    />
                  </div>
                </article>
              ) : null}
            </div>
          </section>
        </>
      ) : selection.range === "custom" ? (
        <section className="page-section">
          <p className="empty-state">
            Pick a start and end date, then press Apply.
          </p>
        </section>
      ) : null}
    </>
  );
}
